import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, ErrorBox, Loading, Progress } from './ui'
import WeekdayTable from './WeekdayTable'
import { updatePnlSettings } from '../../lib/pnlApi'
import { PNL_KEY } from './queries'
import { money, pct, isNum, safeDiv, negClass, errMsg } from './format'

function TargetRow({ name, value, target, fmt }) {
  const ratio = isNum(value) && isNum(target) && Number(target) !== 0 ? Number(value) / Number(target) : NaN
  return (
    <>
      <div className="flex justify-between text-[13px] gap-2">
        <span>{name}</span>
        <span className="tabular-nums">
          <span className={negClass(value)}>{fmt(value)}</span>{' '}
          <span className="text-slate-500">of {isNum(target) ? fmt(target) : 'no target set'}</span>
        </span>
      </div>
      <Progress ratio={ratio} />
    </>
  )
}

function EditTargets({ targets, onDone, addToast }) {
  const qc = useQueryClient()
  const [pbitMonth, setPbitMonth] = useState(isNum(targets?.pbit_month) ? String(targets.pbit_month) : '')
  const [pbitPerOrder, setPbitPerOrder] = useState(isNum(targets?.pbit_per_order) ? String(targets.pbit_per_order) : '')
  // Stored as a fraction (0.35), edited as a percentage (35).
  const [gm, setGm] = useState(isNum(targets?.gross_margin) ? String(+(Number(targets.gross_margin) * 100).toFixed(2)) : '')

  const toNum = (s) => (s === '' || s === null ? null : Number(s))
  const save = useMutation({
    mutationFn: () => updatePnlSettings({
      targets: {
        pbit_month: toNum(pbitMonth),
        pbit_per_order: toNum(pbitPerOrder),
        gross_margin: gm === '' ? null : Number(gm) / 100,
      },
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PNL_KEY, 'settings'] })
      addToast({ type: 'success', message: 'Targets saved' })
      onDone()
    },
    onError: (e) => addToast({ type: 'error', message: `Couldn't save targets: ${errMsg(e)}` }),
  })

  const bad = [pbitMonth, pbitPerOrder, gm].some((s) => s !== '' && !Number.isFinite(Number(s)))
  const field = 'border border-slate-300 rounded-md px-2 py-1.5 text-[13px]'
  return (
    <form
      className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2"
      onSubmit={(e) => { e.preventDefault(); if (!bad) save.mutate() }}
    >
      <label className="text-[12.5px] text-slate-700 flex flex-col gap-1">PBIT per month ($)
        <input className={field} type="number" step="100" value={pbitMonth} onChange={(e) => setPbitMonth(e.target.value)} />
      </label>
      <label className="text-[12.5px] text-slate-700 flex flex-col gap-1">PBIT per order ($)
        <input className={field} type="number" step="0.05" value={pbitPerOrder} onChange={(e) => setPbitPerOrder(e.target.value)} />
      </label>
      <label className="text-[12.5px] text-slate-700 flex flex-col gap-1">Gross margin (%)
        <input className={field} type="number" step="0.5" value={gm} onChange={(e) => setGm(e.target.value)} />
      </label>
      <div className="sm:col-span-3 flex gap-2">
        <button type="submit" className="btn-primary !py-1.5" disabled={bad || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save targets'}
        </button>
        <button type="button" className="btn-secondary !py-1.5" onClick={onDone}>Cancel</button>
      </div>
    </form>
  )
}

function Calculator({ costPerOrder, monthLabel }) {
  const [orders, setOrders] = useState('300')
  const [fees, setFees] = useState('3.50')
  const [ship, setShip] = useState('1.30')
  const [store, setStore] = useState('800')
  const n = Number(orders) || 0
  const f = Number(fees) || 0
  const s = Number(ship) || 0
  const st = Number(store) || 0
  const known = isNum(costPerOrder)
  const rev = n * (f + s) + st
  const cost = known ? n * Number(costPerOrder) : null
  const prof = known ? rev - cost : null
  // Fees per order that make profit zero: costPerOrder − shipping margin − storage per order.
  const be = known && n > 0 ? Number(costPerOrder) - s - st / n : null
  const field = 'border border-slate-300 rounded-md px-2 py-1.5 text-[13px]'
  const Row = ({ label, value, cls = '', bold }) => (
    <div className={`flex justify-between py-0.5 text-[13px] tabular-nums gap-2 ${bold ? 'font-bold border-t border-slate-300 mt-1 pt-1.5' : ''}`}>
      <span>{label}</span><span className={cls}>{value}</span>
    </div>
  )
  return (
    <Card
      title="Price a new client"
      sub={<>What a new client would earn you, using {monthLabel}&apos;s cost per order (<b>{money(costPerOrder, 2)}</b> of wages and overheads per order, your order-share rule).</>}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3.5 gap-y-2 mb-3">
        <label className="text-[12.5px] text-slate-700 flex flex-col gap-1">Orders per month
          <input className={field} type="number" value={orders} onChange={(e) => setOrders(e.target.value)} />
        </label>
        <label className="text-[12.5px] text-slate-700 flex flex-col gap-1">Fulfilment fees per order ($)
          <input className={field} type="number" step="0.05" value={fees} onChange={(e) => setFees(e.target.value)} />
        </label>
        <label className="text-[12.5px] text-slate-700 flex flex-col gap-1">Shipping margin per order ($)
          <input className={field} type="number" step="0.05" value={ship} onChange={(e) => setShip(e.target.value)} />
        </label>
        <label className="text-[12.5px] text-slate-700 flex flex-col gap-1">Storage &amp; other monthly charges ($)
          <input className={field} type="number" step="50" value={store} onChange={(e) => setStore(e.target.value)} />
        </label>
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
        <Row label="Earnings (fees + shipping margin + storage)" value={money(rev)} />
        <Row label="Share of wages & overheads" value={known ? money(-cost) : '—'} />
        <Row label="Profit per month" value={money(prof)} cls={negClass(prof)} bold />
        <Row label="Profit per order" value={n ? money(safeDiv(prof, n), 2) : '—'} cls={negClass(prof)} />
        <Row label="Fees per order needed to break even" value={money(be, 2)} />
      </div>
      {!known && (
        <p className="text-xs text-amber-700 mt-2">
          Cost per order is unknown until {monthLabel}&apos;s wages and overheads are in from Xero. Step back to a closed month.
        </p>
      )}
    </Card>
  )
}

/**
 * settingsQ = usePnlSettings(); monthQ = the MONTH statement for the selected
 * month (targets are monthly); dailyQ = the period's daily query (weekday table).
 */
export default function TargetsTab({ settingsQ, monthQ, dailyQ, monthLabel, addToast }) {
  const [editing, setEditing] = useState(false)
  const k = monthQ.data?.kpis || {}
  const targets = settingsQ.data?.targets || {}
  // Same cost the Orders/Daily tabs share across orders: labour + overheads +
  // Xero cost-of-sales accounts (older API responses lack the last → 0).
  const costPerOrder = safeDiv(
    isNum(k.wages_super) && isNum(k.overheads)
      ? Number(k.wages_super) + Number(k.overheads) + (isNum(k.xero_cost_of_sales) ? Number(k.xero_cost_of_sales) : 0)
      : null,
    k.orders,
  )

  return (
    <>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card title="Targets" sub={`Progress for ${monthLabel}. The Daily tab draws the monthly PBIT target as a line per working day.`}>
          {settingsQ.error ? <ErrorBox error={settingsQ.error} what="targets" />
            : monthQ.error ? <ErrorBox error={monthQ.error} what={`${monthLabel} figures`} />
              : (settingsQ.isLoading && !settingsQ.data) || (monthQ.isLoading && !monthQ.data) ? <Loading />
                : (
                  <>
                    <TargetRow name="PBIT this month" value={k.pbit} target={targets.pbit_month} fmt={(v) => money(v)} />
                    <TargetRow name="PBIT per order" value={k.pbit_per_order} target={targets.pbit_per_order} fmt={(v) => money(v, 2)} />
                    <TargetRow name="Gross margin" value={k.gross_margin} target={targets.gross_margin} fmt={(v) => pct(v)} />
                    {editing
                      ? <EditTargets targets={targets} onDone={() => setEditing(false)} addToast={addToast} />
                      : <button type="button" className="btn-secondary !py-1.5" onClick={() => setEditing(true)}>Edit targets</button>}
                  </>
                )}
        </Card>
        <Calculator costPerOrder={costPerOrder} monthLabel={monthLabel} />
      </div>
      <Card title="Break-even by weekday" sub="Same numbers as the Daily tab, for planning next week's roster.">
        {dailyQ.error ? <ErrorBox error={dailyQ.error} what="weekday averages" />
          : dailyQ.isLoading && !dailyQ.data ? <Loading />
            : <WeekdayTable weekdays={dailyQ.data?.weekdays} />}
      </Card>
    </>
  )
}
